<template>
  <div class="banking-context-selector">
    <Popover v-slot="{ open }" as="div" class="relative h-full">
      <PopoverButton
        class="banking-context-trigger-button h-full border-0 p-0 disabled:cursor-wait disabled:opacity-70"
        :class="{ 'banking-context-trigger-button--open': open }"
        :aria-expanded="open"
        :disabled="loadingBusinesses || loadingPersonalAccounts">
        <div class="banking-context-trigger">
          <div class="banking-context-trigger-text">
            <div class="banking-context-trigger-name">{{ customerFirstName }}</div>
            <div v-if="activeContextLabel" class="banking-context-trigger-context">{{ activeContextLabel }}</div>
          </div>
          <div class="banking-context-trigger-icon" aria-hidden="true">
            <MDropDownIcon />
          </div>
        </div>
      </PopoverButton>

      <transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="scale-95 transform opacity-0"
        enter-to-class="scale-100 transform opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="scale-100 transform opacity-100"
        leave-to-class="scale-95 transform opacity-0">
        <PopoverPanel
          v-slot="{ close }"
          class="absolute right-0 z-50 mt-1 w-[17.5rem] origin-top-right overflow-hidden rounded-lg bg-surface shadow-xl">
          <ul v-if="hasBusinessContexts" role="listbox" class="m-0 list-none p-0">
            <li v-if="hasPersonalAccounts" role="option" :aria-selected="isPersonalSelected">
              <button
                type="button"
                class="banking-context-option"
                :class="{ 'banking-context-option--active': isPersonalSelected }"
                @click="onSelectContext(PERSONAL_BANKING_CONTEXT, close)">
                <span class="banking-context-option-label">{{ t("bankingContext_personal") }}</span>
              </button>
            </li>

            <li v-for="business in businesses" :key="business.id" role="option" :aria-selected="isBusinessSelected(business.id)">
              <button
                type="button"
                class="banking-context-option"
                :class="{ 'banking-context-option--active': isBusinessSelected(business.id) }"
                @click="onSelectContext({ type: BankingContextType.Business, entityId: business.id }, close)">
                <span class="banking-context-option-label">{{ business.name }}</span>
                <span class="banking-context-option-sublabel">{{ t("bankingContext_business") }}</span>
              </button>
            </li>
          </ul>

          <div :class="{ 'border-t border-state-disabled-light': hasBusinessContexts }">
            <router-link :to="{ name: routeNames.MESSAGES }" class="banking-context-footer-link" @click="close">
              <img :src="messagesIcon" alt="" aria-hidden="true" class="h-6 w-6 shrink-0" />
              <span>{{ t("navigation_messages") }}</span>
            </router-link>
            <router-link :to="{ name: routeNames.USER_PROFILE }" class="banking-context-footer-link" @click="close">
              <img :src="cogIcon" alt="" aria-hidden="true" class="h-6 w-6 shrink-0" />
              <span>{{ t("bankingContext_profileSettings") }}</span>
            </router-link>
          </div>
        </PopoverPanel>
      </transition>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import { MDropDownIcon, useIntl } from "@moroku/money-webapp-components";
import { useBankingContextStore } from "@/stores/bankingContext";
import { BankingContextType, type BankingContext, PERSONAL_BANKING_CONTEXT } from "@/types/bankingContext";
import { useCustomer } from "@/composables/useCustomer";
import { routeNames } from "@/router/constants";
import cogIcon from "primeicons/raw-svg/cog.svg";
import messagesIcon from "primeicons/raw-svg/envelope.svg";

const { t } = useIntl();
const bankingContextStore = useBankingContextStore();
const { customer } = useCustomer();

const businesses = computed(() => bankingContextStore.businesses);
const loadingBusinesses = computed(() => bankingContextStore.loadingBusinesses);
const loadingPersonalAccounts = computed(() => bankingContextStore.loadingPersonalAccounts);
const hasBusinessContexts = computed(() => businesses.value.length > 0);
const hasPersonalAccounts = computed(() => bankingContextStore.hasPersonalAccounts);
const isPersonalSelected = computed(() => bankingContextStore.context.type === BankingContextType.Personal);
const customerFirstName = computed(() => customer.value?.firstName?.trim() || t("navigation_profile"));
const activeContextLabel = computed(() => {
  // Personal-only customers have nothing to switch — don't show a redundant "Personal Banking" label.
  if (!hasBusinessContexts.value) {
    return null;
  }
  if (bankingContextStore.context.type === BankingContextType.Personal) {
    return t("bankingContext_personal");
  }
  return bankingContextStore.activeBusinessName ?? t("bankingContext_business");
});

const isBusinessSelected = (entityId: string) => {
  const { context } = bankingContextStore;
  return context.type === BankingContextType.Business && context.entityId === entityId;
};

const onSelectContext = async (next: BankingContext, close: () => void) => {
  close();
  await bankingContextStore.setBankingContext(next);
};
</script>

<style scoped lang="scss">
.banking-context-selector {
  @apply h-14 shrink-0;
}

:deep(.banking-context-trigger-button) {
  @apply bg-transparent text-left outline-none;
  color: var(--color-text-primary, #1a1a1a);

  // Hover only — Headless refocuses this button on close, so avoid :focus backgrounds.
  &:hover:not(:disabled) {
    background-color: #e5e5e5;
  }

  &.banking-context-trigger-button--open {
    @apply bg-secondary text-on-secondary;

    &:hover:not(:disabled) {
      @apply bg-secondary;
    }
  }
}

.banking-context-trigger {
  @apply flex h-full items-center gap-4 px-5;
}

.banking-context-trigger-text {
  @apply min-w-0 overflow-hidden;
}

.banking-context-trigger-name,
.banking-context-trigger-context {
  @apply truncate text-sm leading-normal;
}

.banking-context-trigger-name {
  @apply font-medium;
}

.banking-context-trigger-context {
  @apply font-bold;
}

.banking-context-trigger-icon {
  @apply w-3 shrink-0;
}

.banking-context-option {
  @apply flex w-full flex-col items-start border-0 bg-transparent px-5 py-4 text-left outline-none;
  color: var(--color-text-primary);

  &:hover,
  &:focus-visible {
    @apply bg-surface-light;
  }

  &--active {
    @apply bg-secondary text-on-secondary;

    // Selected row keeps the secondary fill on hover (generic hover is more specific otherwise).
    &:hover,
    &:focus-visible {
      @apply bg-secondary;
    }

    .banking-context-option-sublabel {
      @apply text-on-secondary;
    }
  }
}

.banking-context-option-label,
.banking-context-option-sublabel {
  @apply block w-full truncate leading-5;
}

.banking-context-option-label {
  @apply text-sm font-semibold;
}

.banking-context-option-sublabel {
  @apply text-xs font-medium text-[#5a5a5a];
}

.banking-context-footer-link {
  @apply flex h-14 items-center gap-2 px-5 text-sm font-semibold leading-5;
  color: var(--color-text-primary, #1a1a1a);

  &:hover,
  &:focus-visible {
    @apply bg-surface-light outline-none;
  }
}
</style>
